import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  ModalCloseButton,
  Spinner,
  ModalBody,
  ModalFooter,
  Button,
  useToast,
} from '@chakra-ui/react';
import { samplesApi } from '@texas/api/endpoints/samplesApi';
import { EntityType, trackerApi } from '@texas/api/endpoints/trackerApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '@texas/utils/helpers/httpHelpers';
import { SamplesTable } from './SamplesTable';
import { ErrorDetails } from '../alert/ErrorDetails';
import { SharedDisclosureProps } from '../types';
import { entityTypeFriendlyName } from '../tracker/translations';

interface Props extends SharedDisclosureProps {
  sampleIds: number[];
  onPerformed: () => void;
}

export function StopFollowSamplesModal(props: Props) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('sample.stopFollow')}</ModalHeader>
        <ModalCloseButton />
        <Inner
          sampleIds={props.sampleIds}
          onPerform={() => {
            props.onPerformed();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  sampleIds,
  onPerform,
}: {
  sampleIds: number[];
  onPerform: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const {
    data: samples,
    refetch: refetchSamples,
    loading: samplesLoading,
    error,
  } = useApiResource(samplesApi.fromIds);

  const {
    request: mainReq,
    loading: mainReqLoading,
    error: mainReqError,
  } = useApiRequest(trackerApi.unsubscribeTrackers);

  useEffect(() => {
    refetchSamples(sampleIds);
  }, [refetchSamples, sampleIds]);

  const req = () =>
    request(mainReq, [sampleIds, EntityType.Sample], (response) => {
      if (response.failedUnsubscribedIds.length > 0) {
        toast({
          status: 'warning',
          title: t('tracking.untrackErrorTitle'),
          description: t('tracking.untrackErrorDesc', {
            count: response.failedUnsubscribedIds.length,
            type: entityTypeFriendlyName(t, response.entityType),
          }),
        });
      }
      onPerform();
    });

  return (
    <>
      <ModalBody>
        <Flex flexDir="column" gap={2} w="full">
          {error && <ErrorDetails error={error} />}
          {mainReqError && <ErrorDetails error={mainReqError} />}
          {samplesLoading && <Spinner />}
          <SamplesTable samples={samples ?? []} />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          isLoading={mainReqLoading}
          disabled={mainReqLoading}
          onClick={() => req()}
        >
          {t('sample.stopFollowConfirm')}
        </Button>
      </ModalFooter>
    </>
  );
}
